'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { blogService } from '@/services/blogService';
import { BlogPost } from '@/lib/supabase';
import Icon from '@/components/ui/AppIcon';
import MarkdownRenderer from '@/app/blog/MarkdownRenderer';
import HTMLRenderer from '@/app/blog/HTMLRenderer'; // Import HTMLRenderer
import BlogComments from '@/app/blog/BlogComments';
import { useAuth } from '@/contexts/AuthContext';

const BlogPostPage = () => {
  const { isAdmin } = useAuth();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const params = useParams();
  const slug = params?.slug as string;

  useEffect(() => {
    if (slug) {
      loadBlogPost(slug);
    }
  }, [slug]);

  // SEO Metadata injection
  useEffect(() => {
    if (!post) return;

    const seoTitle = post.seo_title || post.title;
    const seoDescription = post.seo_description || post.excerpt || post.content.substring(0, 160);
    const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const fullUrl = `${siteUrl}/blog/${post.slug}`;
    const imageUrl = post.featured_image_url || `${siteUrl}/og-image.jpg`;

    // Update document title
    document.title = seoTitle.length > 60 ? seoTitle.substring(0, 57) + '...' : seoTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary meta tags
    updateMetaTag('description', seoDescription.length > 160 ? seoDescription.substring(0, 157) + '...' : seoDescription);
    if (post.seo_keywords) {
      updateMetaTag('keywords', post.seo_keywords);
    }
    if (post.author?.name) {
      updateMetaTag('author', post.author.name);
    }

    // Open Graph tags
    updateMetaTag('og:title', seoTitle, 'property');
    updateMetaTag('og:description', seoDescription, 'property');
    updateMetaTag('og:image', imageUrl, 'property');
    updateMetaTag('og:url', fullUrl, 'property');
    updateMetaTag('og:type', 'article', 'property');
    if (post.published_at) {
      updateMetaTag('article:published_time', new Date(post.published_at).toISOString(), 'property');
    }
    if (post.author?.name) {
      updateMetaTag('article:author', post.author.name, 'property');
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', seoTitle);
    updateMetaTag('twitter:description', seoDescription);
    updateMetaTag('twitter:image', imageUrl);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);
  }, [post]);

  const loadBlogPost = async (postSlug: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await blogService.fetchBySlug(postSlug);

      if (result.error) {
        if (result.error.code === 'PGRST116') {
          setError('Blog post not found');
        } else {
          throw result.error;
        }
        return;
      }

      if (!result.data) {
        setError('Blog post not found');
        return;
      }

      setPost(result.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load blog post';
      setError(errorMessage);
      console.error('Error loading blog post:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-muted rounded w-1/4 mb-6"></div>
            <div className="h-10 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-muted rounded w-1/3 mb-8"></div>
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-4 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="ExclamationTriangleIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">Post Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link
            href="/blog"
            className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={20} className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <nav className="mb-8 flex justify-between items-center">
          <Link
            href="/blog"
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={16} className="mr-1" />
            Back to Blog
          </Link>
          {isAdmin && post?.slug && (
            <Link
              href={`/blog/admin/edit/${post.slug}`}
              className="inline-flex items-center px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 font-medium transition-colors"
            >
              <Icon name="PencilIcon" size={16} className="mr-2" />
              Edit Post
            </Link>
          )}
        </nav>

        {/* Featured Image */}
        {post.featured_image_url && (
          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={post.featured_image_url}
              alt={post.featured_image_alt_text || post.title}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Article Header */}
        <header className="mb-8">
          {/* H1 - Main Title (only one per page) */}
          <h1 className="text-4xl font-heading font-bold text-foreground mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
              {post.excerpt}
            </p>
          )}
          
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm">
            <div className="flex items-center">
              <Icon name="UserIcon" size={16} className="mr-2" />
              <span>By {post.author?.name || 'Unknown Author'}</span>
            </div>
            <div className="flex items-center">
              <Icon name="CalendarIcon" size={16} className="mr-2" />
              <span>Published {formatDate(post.published_at)}</span>
            </div>
            {post.reading_time && (
              <div className="flex items-center">
                <Icon name="ClockIcon" size={16} className="mr-2" />
                <span>{post.reading_time} min read</span>
              </div>
            )}
            {post.view_count !== undefined && (
              <div className="flex items-center">
                <Icon name="EyeIcon" size={16} className="mr-2" />
                <span>{post.view_count} views</span>
              </div>
            )}
          </div>
        </header>

        {/* Article Content */}
        <article className="mb-12">
          <div className="bg-card rounded-lg border border-border p-8">
            {post.content_type === 'html' ? (
              <HTMLRenderer html={post.content} />
            ) : (
              <MarkdownRenderer content={post.content} />
            )}
          </div>
        </article>

        {/* Article Footer */}
        <footer className="mb-12 pt-8 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                <Icon name="UserIcon" size={24} className="text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{post.author?.name || 'Unknown Author'}</p>
                <p className="text-sm text-muted-foreground">Author</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      text: post.excerpt || post.seo_description,
                      url: window.location.href
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary/10 rounded-lg transition-colors"
                title="Share this post"
              >
                <Icon name="ShareIcon" size={20} />
              </button>
            </div>
          </div>
        </footer>

        {/* Comments Section */}
        <BlogComments postId={post.id} />

        {/* Call to Action */}
        <div className="mt-16">
          <div className="bg-primary/10 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Ready to Optimize Your Space?
            </h3>
            <p className="text-muted-foreground mb-6">
              Get personalized AI-powered recommendations for your specific space
            </p>
            <Link
              href="/input-form"
              className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
            >
              <Icon name="PlusCircleIcon" size={20} className="mr-2" />
              Create Your Plan
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
